// src/components/PostFormModal.js

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { createPost, fetchPost, updatePost } from "../services/postService";

function PostFormModal({ show, handleClose, postId = null, onPostSaved }) {
  const { user } = useAuth();
  const isEditMode = !!postId;

  const [formData, setFormData] = useState({
    title: "",
    content: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(isEditMode);

  useEffect(() => {
    // Reset form when modal opens/closes
    if (show) {
      if (isEditMode) {
        setLoading(true);
        fetchPost(postId)
          .then(postData => {
            setFormData({
              title: postData.title || "",
              content: postData.content || ""
            });
            setLoading(false);
          })
          .catch(err => {
            setError("Napaka pri nalaganju objave.");
            setLoading(false);
          });
      } else {
        // New post - reset form
        setFormData({
          title: "",
          content: ""
        });
        setError(null);
      }
    }
  }, [show, postId, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.content.trim()) {
      setError("Vsebina objave je obvezna.");
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      let result;
      if (isEditMode) {
        result = await updatePost(postId, formData);
      } else {
        result = await createPost(formData);
      }
      
      setIsSubmitting(false);
      
      // Call the callback function with the saved post
      if (onPostSaved) {
        onPostSaved(result.post);
      }
      
      // Close the modal
      handleClose();
    } catch (err) {
      setError("Napaka pri shranjevanju objave. Poskusite ponovno.");
      setIsSubmitting(false);
    }
  };

  // If the modal isn't shown, don't render anything
  if (!show) {
    return null;
  }

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditMode ? "Uredi objavo" : "Nova objava"}
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              disabled={isSubmitting}
            ></button>
          </div>
          
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">
                    Naslov (neobvezno)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Vnesi naslov objave"
                  />
                </div>
                
                <div className="mb-3">
                  <label htmlFor="content" className="form-label">
                    Vsebina <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    id="content"
                    name="content"
                    rows="6"
                    value={formData.content}
                    onChange={handleChange}
                    placeholder="Vnesi vsebino objave..."
                    required
                  ></textarea>
                </div>
              </form>
            )}
          </div>
          
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClose}
              disabled={isSubmitting || loading}
            >
              Prekliči
            </button>
            
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || loading || !formData.content.trim()}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Shranjevanje...
                </>
              ) : isEditMode ? (
                "Shrani spremembe"
              ) : (
                "Objavi"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PostFormModal;